import { Box, Stack, Typography } from "@mui/material";
import { type ReactNode } from "react";

import { getYoutubeEmbedUrl } from "@/shared/utils/youtube/youtubeEmbedUrl";
import { HOPE_SANS } from "@/shared/branding/thehopeonebrand";

import type { AnchorCube, AnchorCubeColumn } from "../utils/anchorCubes";
import { cubeSectionKey } from "../utils/anchorCubes";
import {
  columnExperienceHasContent,
  columnImageUrls,
  type ImpactColumnExperience,
  type ImpactColumnExperienceType,
} from "../utils/columnExperience";
import { columnAnchorDomId } from "../utils/impactPages";
import {
  ImpactStoryGallery,
  ImpactStoryText,
  ImpactStoryVideo,
  storyCaptionSx,
  storyTitleSx,
} from "./ImpactStoryGallery";

const c = {
  forestDeep: "#102C24",
  forest: "#1D4437",
  green: "#5E8F72",
  muted: "#5A6B63",
  ivory: "#FAF8F2",
  paper: "#FFFFFF",
  border: "rgba(16,44,36,.10)",
};

function displayTitle(column: AnchorCubeColumn): string {
  const title = column.title?.trim();
  if (!title || title.toLowerCase() === "untitled") return "";
  return title;
}

export function StoryMedia({
  type,
  experience,
  heading,
}: {
  type?: ImpactColumnExperienceType;
  experience?: ImpactColumnExperience;
  heading?: string;
}) {
  const youtubeUrls = [
    experience?.url,
    ...(experience?.urls ?? []),
  ].filter((url, index, list): url is string => Boolean(url) && list.indexOf(url) === index);
  /** Shorts are vertical, so they get a portrait frame instead of a pillarboxed one. */
  const youtubeEmbeds = youtubeUrls
    .map((url) => ({ src: getYoutubeEmbedUrl(url), vertical: /\/shorts\//i.test(url) }))
    .filter((item): item is { src: string; vertical: boolean } => Boolean(item.src));
  if (type === "Youtube" && youtubeEmbeds.length) {
    return (
      <Stack spacing={1.25} sx={{ width: "100%" }}>
        {youtubeEmbeds.map((item, index) => (
          <Box
            key={item.src}
            sx={{
              position: "relative",
              borderRadius: "16px",
              overflow: "hidden",
              bgcolor: "#102C24",
              aspectRatio: item.vertical ? "9 / 16" : "16 / 9",
              width: "100%",
              maxWidth: item.vertical ? 320 : "none",
              mx: item.vertical ? "auto" : 0,
            }}
          >
            <Box
              component="iframe"
              title={`YouTube ${index + 1}`}
              src={item.src}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
            />
          </Box>
        ))}
      </Stack>
    );
  }

  const youtubeEmbed = experience?.url ? getYoutubeEmbedUrl(experience.url) : null;
  if (youtubeEmbed) {
    const vertical = /\/shorts\//i.test(experience?.url ?? "");
    return (
      <Box
        sx={{
          position: "relative",
          borderRadius: "16px",
          overflow: "hidden",
          bgcolor: "#102C24",
          aspectRatio: vertical ? "9 / 16" : "16 / 9",
          width: "100%",
          maxWidth: vertical ? 320 : "none",
          mx: vertical ? "auto" : 0,
        }}
      >
        <Box
          component="iframe"
          title="YouTube"
          src={youtubeEmbed}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
        />
      </Box>
    );
  }

  if (type === "Video" && experience?.url) {
    return <ImpactStoryVideo src={experience.url} />;
  }

  if (type === "Audio" && experience?.url) {
    return (
      <Box
        sx={{
          borderRadius: "18px",
          bgcolor: "#EEF5EF",
          px: 2,
          py: 1.75,
          border: `1px solid ${c.border}`,
        }}
      >
        <Box component="audio" src={experience.url} controls sx={{ width: "100%" }} />
      </Box>
    );
  }

  if (type === "Image") {
    return <ImpactStoryGallery urls={columnImageUrls(experience)} />;
  }

  if (type === "Text") {
    const expTitle = experience?.title?.trim();
    const headingMatch = heading?.trim() && expTitle && heading.trim() === expTitle;
    return (
      <ImpactStoryText
        title={headingMatch ? undefined : expTitle}
        body={experience?.description?.trim()}
      />
    );
  }

  if (type === "Location") {
    const query = experience?.address?.trim() || experience?.title?.trim() || "";
    const embedUrl = query ? `https://maps.google.com/maps?q=${encodeURIComponent(query)}&output=embed` : "";
    return (
      <Stack spacing={1}>
        {experience?.address ? (
          <Typography
            sx={{
              fontFamily: HOPE_SANS,
              fontSize: "1.05rem",
              color: c.muted,
              lineHeight: 1.6,
              overflowWrap: "break-word",
            }}
          >
            {experience.address}
          </Typography>
        ) : null}
        {embedUrl ? (
          <Box
            component="iframe"
            title="Map"
            src={embedUrl}
            sx={{ width: "100%", height: 220, border: 0, borderRadius: "18px" }}
          />
        ) : null}
      </Stack>
    );
  }

  return null;
}

export function findCubeForSection(
  cubes: AnchorCube[],
  section?: string
): AnchorCube | undefined {
  if (!section) return undefined;
  return cubes.find((cube) => cubeSectionKey(cube) === section);
}

export function VerticalBookSheet({
  id,
  children,
  fit = "page",
  snapStop = "always",
}: {
  id?: string;
  children: ReactNode;
  fit?: "page" | "content";
  snapStop?: "always" | "normal";
}) {
  const hug = fit === "content";
  return (
    <Box
      id={id}
      sx={{
        width: "100%",
        height: hug ? "auto" : "100cqh",
        minHeight: hug ? 0 : "100cqh",
        maxHeight: hug ? "none" : "100cqh",
        flexShrink: hug ? undefined : 0,
        boxSizing: "border-box",
        bgcolor: c.ivory,
        scrollSnapAlign: "start",
        scrollSnapStop: hug ? "normal" : snapStop,
        overflow: hug ? "visible" : "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          flex: hug ? "none" : 1,
          minHeight: hug ? 0 : 0,
          overflowY: hug ? "visible" : "auto",
          overscrollBehaviorY: "auto",
          WebkitOverflowScrolling: "touch",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function sheetFit(type?: ImpactColumnExperienceType): "page" | "content" {
  return type === "Text" ? "content" : "page";
}

export function ImpactColumnStack({ cube }: { cube?: AnchorCube }) {
  if (!cube) return null;
  const columns = cube.columns.filter(
    (column) =>
      columnExperienceHasContent(column.experienceType, column.experience) || Boolean(column.pageId)
  );
  if (!cube || columns.length === 0) return null;

  return (
    <>
      {columns.map((column) => {
        const title = displayTitle(column);
        const caption =
          column.experienceType === "Text"
            ? ""
            : column.description?.trim() || column.experience?.description?.trim() || "";
        const hasMedia = columnExperienceHasContent(column.experienceType, column.experience);

        const hug = sheetFit(column.experienceType);

        return (
          <VerticalBookSheet key={column.id} id={columnAnchorDomId(column.id)} fit={hug}>
            <Box
              sx={{
                px: { xs: 2, sm: 2.5 },
                py: { xs: 2, sm: 2.5 },
                display: "flex",
                flexDirection: "column",
                flex: hug === "content" ? "none" : 1,
                minHeight: 0,
              }}
            >
              {title ? (
                <Typography sx={{ ...storyTitleSx, mb: 0.75, flexShrink: 0 }}>
                  {title}
                </Typography>
              ) : null}

              {caption && caption !== title ? (
                <Typography sx={{ ...storyCaptionSx, mb: 2, flexShrink: 0 }}>
                  {caption}
                </Typography>
              ) : null}

              <Box
                sx={{
                  flex: hug === "content" ? "none" : 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent:
                    column.experienceType === "Image"
                      ? "stretch"
                      : column.experienceType === "Video" || column.experienceType === "Youtube"
                        ? "center"
                        : "flex-start",
                }}
              >
                {hasMedia ? (
                  <StoryMedia
                    type={column.experienceType}
                    experience={column.experience}
                    heading={title}
                  />
                ) : null}
              </Box>
            </Box>
          </VerticalBookSheet>
        );
      })}
    </>
  );
}

export function ImpactStackedPage({
  children,
  cube,
  fit = "page",
}: {
  children: ReactNode;
  cube?: AnchorCube;
  fillViewport?: boolean;
  fit?: "page" | "content";
}) {
  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: c.ivory,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        "& > * + *": {
          borderTop: `1px solid ${c.border}`,
        },
      }}
    >
      <VerticalBookSheet fit={fit}>{children}</VerticalBookSheet>
      <ImpactColumnStack cube={cube} />
    </Box>
  );
}

export default function ImpactCubePage({ cube }: { cube?: AnchorCube }) {
  if (!cube) return null;

  const pageTitle = cube.title?.trim() || cube.experience?.title?.trim() || "";
  const pageCaption =
    cube.experienceType === "Text"
      ? ""
      : cube.description?.trim() || cube.experience?.description?.trim() || "";
  const hasPageMedia = columnExperienceHasContent(cube.experienceType, cube.experience);

  const hug = sheetFit(cube.experienceType);

  return (
    <Box sx={{ width: "100%", bgcolor: c.ivory, display: "flex", flexDirection: "column", "& > * + *": { borderTop: `1px solid ${c.border}` } }}>
      <VerticalBookSheet fit={hug}>
        <Box
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: { xs: 2, sm: 2.5 },
            display: "flex",
            flexDirection: "column",
            flex: hug === "content" ? "none" : 1,
            minHeight: 0,
          }}
        >
          {pageTitle ? (
            <Typography sx={{ ...storyTitleSx, mb: 0.75, flexShrink: 0 }}>{pageTitle}</Typography>
          ) : null}
          {pageCaption && pageCaption !== pageTitle ? (
            <Typography sx={{ ...storyCaptionSx, mb: 2, flexShrink: 0 }}>{pageCaption}</Typography>
          ) : null}
          <Box
            sx={{
              flex: hug === "content" ? "none" : 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent:
                cube.experienceType === "Image"
                  ? "stretch"
                  : cube.experienceType === "Video" || cube.experienceType === "Youtube"
                    ? "center"
                    : "flex-start",
            }}
          >
            {hasPageMedia ? (
              <StoryMedia type={cube.experienceType} experience={cube.experience} heading={pageTitle} />
            ) : null}
          </Box>
        </Box>
      </VerticalBookSheet>
      <ImpactColumnStack cube={cube} />
    </Box>
  );
}
